// HydraID — клиентская часть глобального номера человека `AAA BBB CCC D`.
//
// SSoT алгоритма — сервер (`zdc-server/internal/hydraid/verhoeff.go`); здесь
// ЗЕРКАЛО Verhoeff (та же спецификация), чтобы проверять контрольную цифру
// ЛОКАЛЬНО, ДО сети (ошибки ввода не уходят на сервер). Векторы совместимости —
// REFERENCE_VECTORS (те же значения проверяет серверный тест).
//
// Формат: AAA — префикс базы (локальный MVP 900–999), BBB CCC — счётчик базы,
// D — контрольная цифра Verhoeff. Отображение — «AAA BBB CCC D».

export const NUMBER_LENGTH = 10;
export const PREFIX_LENGTH = 3;
export const COUNTER_DIGITS = 6;
export const MAX_COUNTER = 999999;
export const LOCAL_PREFIX_BASE = 900;

// Эталонные таблицы Verhoeff (диэдральная группа D5) — те же, что на сервере.
const D_TABLE: readonly (readonly number[])[] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const P_TABLE: readonly (readonly number[])[] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

const INV_TABLE: readonly number[] = [0, 4, 3, 2, 1, 5, 6, 7, 8, 9];

/** Векторы совместимости клиент↔сервер (те же входы/выходы, что на сервере). */
export const REFERENCE_VECTORS: Readonly<Record<string, number>> = {
  "236": 3,
  "12345": 1,
  "000000000": 0,
  "123456789": 0,
  "900000001": 8,
  "900123456": 0,
  "999999999": 0,
};

/** Строка состоит только из цифр 0-9 (не пустая). */
export function isDigits(value: string): boolean {
  if (value.length === 0) {
    return false;
  }
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 48 || code > 57) {
      return false;
    }
  }
  return true;
}

/** Контрольная цифра Verhoeff для строки цифр (первые 9 цифр номера). */
export function generateCheckDigit(digits: string): number {
  if (!isDigits(digits)) {
    throw new Error(`hydraid: ожидается строка цифр, получено "${digits}"`);
  }
  let c = 0;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    const pos = (digits.length - i) % 8;
    c = D_TABLE[c][P_TABLE[pos][digits.charCodeAt(i) - 48]];
  }
  return INV_TABLE[c];
}

/** Проверка номера целиком (включая контрольную цифру) по Verhoeff. */
export function validate(number: string): boolean {
  if (number.length < 2 || !isDigits(number)) {
    return false;
  }
  let c = 0;
  for (let i = number.length - 1; i >= 0; i -= 1) {
    const pos = (number.length - 1 - i) % 8;
    c = D_TABLE[c][P_TABLE[pos][number.charCodeAt(i) - 48]];
  }
  return c === 0;
}

/** Оставляет только цифры (ввод/маска/поиск). */
export function normalize(input: string): string {
  let out = "";
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    if (ch >= "0" && ch <= "9") {
      out += ch;
    }
  }
  return out;
}

/** Канонический 10-значный номер: префикс(3) + счётчик(6) + Verhoeff(1). */
export function format(prefix: string, counter: number): string {
  if (prefix.length !== PREFIX_LENGTH || !isDigits(prefix)) {
    throw new Error(`hydraid: префикс должен быть 3 цифрами, получено "${prefix}"`);
  }
  if (counter < 1 || counter > MAX_COUNTER) {
    throw new Error(`hydraid: счётчик вне диапазона 1..${MAX_COUNTER}: ${counter}`);
  }
  const body = prefix + String(counter).padStart(COUNTER_DIGITS, "0");
  return `${body}${generateCheckDigit(body)}`;
}

/** Человекочитаемый вид «AAA BBB CCC D». */
export function display(number: string): string {
  const norm = normalize(number);
  if (norm.length !== NUMBER_LENGTH) {
    throw new Error(`hydraid: номер должен быть ${NUMBER_LENGTH} цифрами, получено "${number}"`);
  }
  return `${norm.slice(0, 3)} ${norm.slice(3, 6)} ${norm.slice(6, 9)} ${norm.slice(9, 10)}`;
}

/**
 * Прогрессивная маска ввода «AAA BBB CCC D» (до 10 цифр). Возвращает строку с
 * пробелами-разделителями; лишние цифры отбрасываются (максимум 10).
 */
export function maskInput(input: string): string {
  const digits = normalize(input).slice(0, NUMBER_LENGTH);
  const groups = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9), digits.slice(9, 10)];
  return groups.filter((g) => g.length > 0).join(" ");
}

/** Готов ли ввод к проверке/поиску: ровно 10 цифр И верная контрольная цифра. */
export function isComplete(number: string): boolean {
  const norm = normalize(number);
  return norm.length === NUMBER_LENGTH && validate(norm);
}
